import React, { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'
import { Input } from 'antd'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { MaxCharactersWrapper } from '../../formik-component/MaxCharactersWrapper'
import { useTargetsModalStore } from './TargetsModalStore'
import { TargetModalFormErrors } from './types'
import { TargetsModalAttachment } from './TargetsModalAttachment'
import { ObjectiveStatus, KeyResultDto, ObjectiveInput } from '../../../types'
import './index.less'
import { TargetsModalComment } from './TargetsModalComment'
import { TargetsModalCommentInput } from './TargetsModalCommentInput'

const MAX_CHARS = 4000

const { TextArea } = Input

const getTasksLength = (tasks: Array<KeyResultDto>) => tasks.map((task) => task.text).join('').length

const TaskCharacterCounter = ({ control }: { control: Control<ObjectiveInput> }) => {
  const { t } = useTranslation()
  const tasks = useWatch({ control, name: 'keyResults' })
  return !!tasks && !!tasks.length ? (
    <PerText fontSize="xs" color="secondary" align="right" mt="xxxs">
      {`${t('targetModal.totalOfCharInTasks')} ${getTasksLength(tasks)} / ${MAX_CHARS}`}
    </PerText>
  ) : null
}

export const TargetsModalFormSuggestedTarget: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { utcDateStringToMoment, calcStartOfNextNYear, calcEndOfNextNYear, calcStartOfTheYearMoment, calcEndOfTheYearMoment } =
    useDateTime(user)
  const [currentObjective, setNewTargetForm, currentComments, modalProps] = useTargetsModalStore(
    (state) => [state.currentObjective, state.setNewTargetForm, state.currentComments, state.modalProps],
    shallow,
  )
  const { meeting } = modalProps

  const newTargetForm = useForm<ObjectiveInput>({
    defaultValues: !!currentObjective
      ? currentObjective
      : {
          keyResults: [{}],
          start: !!meeting ? calcStartOfTheYearMoment(meeting.appraisalSeason.year).toISOString() : calcStartOfNextNYear(0).toISOString(),
          due: !!meeting ? calcEndOfTheYearMoment(meeting.appraisalSeason.year).toISOString() : calcEndOfNextNYear(0).toISOString(),
          status: ObjectiveStatus.Proposal,
        },
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
  } = newTargetForm

  const { fields, remove, append } = useFieldArray({
    name: 'keyResults',
    keyName: 'id',
    control,
  })

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const hasValidSelection = e.currentTarget.selectionStart !== e.currentTarget.selectionEnd
    if (getTasksLength(getValues().keyResults) >= MAX_CHARS) {
      if (!hasValidSelection) {
        e.preventDefault()
      }
    }
  }

  const handleOnPaste = (e: React.ClipboardEvent) => {
    const currentChars = getTasksLength(getValues().keyResults)
    if (currentChars + e.clipboardData.getData('text/plain').length >= MAX_CHARS) {
      e.preventDefault()
    }
  }

  const hasError = (errors: TargetModalFormErrors, fieldName: string, index: number) => {
    if (index > -1) {
      return !!errors && !!errors.keyResults && !!errors.keyResults[index] && !!errors.keyResults[index][fieldName]
    }
    return !!errors && !!errors[fieldName]
  }

  useEffect(() => {
    setNewTargetForm(newTargetForm)
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PerFlexRow direction="column">
        <PerText appearance="label-small" mb="xxs">
          {t('targetModal.title')}
        </PerText>
        <Controller
          name="title"
          control={control}
          rules={{ required: `${t('formError.generalRequired')}` }}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={hasError(errors as TargetModalFormErrors, 'title', -1) ? 'has-error' : ''} style={{ width: '100%' }}>
                <TextArea
                  value={value || undefined}
                  onChange={onChange}
                  rows={1}
                  maxLength={MAX_CHARS}
                  style={{ width: '100%', lineHeight: 1.6 }}
                  placeholder={t('targetModal.titlePlaceholder')}
                  data-e2e-target-title
                />
              </div>
              <PerFlexRow align="middle" justify="space-between" className="fullWidth">
                <PerFlexRow justify="start">
                  <ErrorMessage
                    errors={errors}
                    name="title"
                    render={({ messages }) => {
                      return (
                        <PerText fontSize="xs" color="error">
                          {!!messages && !!messages.required && messages.required}
                        </PerText>
                      )
                    }}
                  />
                </PerFlexRow>
                <PerFlexRow justify="end">
                  <MaxCharactersWrapper usedCharacters={!!value ? value.length : 0} maxCharacters={MAX_CHARS} />
                </PerFlexRow>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>

      <PerFlexRow mt="s">
        <PerText appearance="tile-heading">{t('targetModal.tasks')}</PerText>
      </PerFlexRow>

      <PerFlexRow direction="column">
        {fields.map((field, index) => {
          return (
            <React.Fragment key={index}>
              <PerFlexRow mt="s" key={field.id} className="fullWidth" align="middle" justify="start" noWrap>
                <Controller
                  name={`keyResults.${index}.text`}
                  control={control}
                  rules={{
                    required: `${t('formError.generalRequired')}`,
                    minLength: { value: 2, message: `${t('formError.textLength')}` },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <div className={hasError(errors as TargetModalFormErrors, 'text', index) ? 'has-error' : ''} style={{ width: '90%' }}>
                      <TextArea
                        value={value || undefined}
                        onChange={onChange}
                        rows={1}
                        style={{ width: '100%', lineHeight: 1.6 }}
                        onKeyPress={handleKeyPress}
                        onPaste={handleOnPaste}
                        placeholder={t('targetModal.taskPlaceholder')}
                        data-e2e-keyresult-input
                      />
                    </div>
                  )}
                />
                <Controller name={`keyResults.${index}.completed`} control={control} defaultValue={false} render={() => <></>} />
                <PerFlexRow align="middle" justify="start">
                  {fields.length > 1 && (
                    <PerButtonCircle
                      icon="remove_stroke"
                      size="small"
                      variant="pastel"
                      color="secondary"
                      ml="xs"
                      onClick={() => remove(index)}
                      data-e2e-keyresult-button="remove"
                    />
                  )}
                  {index === fields.length - 1 && (
                    <PerButtonCircle
                      icon="add_stroke"
                      size="small"
                      variant="pastel"
                      color="success"
                      ml="xxs"
                      disabled={!!errors.keyResults}
                      onClick={() => {
                        append({ text: '' } as KeyResultDto)
                        trigger()
                      }}
                      data-e2e-keyresult-button="add"
                    />
                  )}
                </PerFlexRow>
              </PerFlexRow>
              <PerFlexRow align="middle" justify="space-between" width="90%">
                <PerFlexRow align="middle" justify="start">
                  <PerText fontSize="xs" color="error" mt="xxxs">
                    <ErrorMessage
                      errors={errors}
                      name={`keyResults.${index}.text`}
                      render={({ messages }) => {
                        return (
                          <>
                            <PerText fontSize="xs" color="error">
                              {!!messages && !!messages.required && messages.required}
                            </PerText>
                            <PerText fontSize="xs" color="error">
                              {!!messages && !!messages.minLength && messages.minLength}
                            </PerText>
                          </>
                        )
                      }}
                    />
                  </PerText>
                </PerFlexRow>
                <PerFlexRow align="middle" justify="end">
                  {index === fields.length - 1 && <TaskCharacterCounter control={control} />}
                </PerFlexRow>
              </PerFlexRow>
            </React.Fragment>
          )
        })}
      </PerFlexRow>

      <PerSpacer mt="xs" />

      <TargetsModalAttachment showDropZone />

      <PerSpacer mb="m" />

      <PerFlexRow direction="column">
        <PerText appearance="tile-heading" mb="xxs">
          {t('targetModal.comments')}
        </PerText>
        <PerText fontSize="xs" color="secondary">
          {t('targetModal.commentsDisclaimer')}
        </PerText>
      </PerFlexRow>

      {currentComments
        .slice()
        .sort((a, b) => {
          const date1 = utcDateStringToMoment(a.date)
          const date2 = utcDateStringToMoment(b.date)
          return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
        })
        .map((note, index) => {
          return <TargetsModalComment comment={note} index={index} key={`${index}${note.date}`} />
        })}

      <TargetsModalCommentInput />
    </>
  )
}
